import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  isUpdaterFn,
  Setter,
  TableStateManager,
} from '@common/components/table/types';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';

function safeNumber(raw: string): number | undefined {
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * A tiny batching layer so multiple updates that happen in the same tick
 * (e.g. setPaginationState + setSortingState) coalesce into a *single*
 * setSearchParams() call. This prevents the classic "last write wins" issue
 * when each updater starts from the same previous URLSearchParams snapshot.
 */
function useSearchParamsBatcher() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queueRef = useRef<Array<(sp: URLSearchParams) => void>>([]);
  const scheduledRef = useRef(false);

  const flush = useCallback(() => {
    if (!queueRef.current.length) return;
    // Always start from the latest URL, then apply all queued mutations.
    const base = new URLSearchParams(searchParams);
    for (const apply of queueRef.current) apply(base);
    queueRef.current = [];
    scheduledRef.current = false;
    setSearchParams(base, { replace: true });
  }, [searchParams, setSearchParams]);

  const enqueue = useCallback(
    (mutator: (sp: URLSearchParams) => void) => {
      queueRef.current.push(mutator);
      if (!scheduledRef.current) {
        scheduledRef.current = true;
        // Microtask flush: coalesce multiple calls in the same render/task.
        Promise.resolve().then(flush);
      }
    },
    [flush],
  );

  const get = useCallback(
    (key: string) => searchParams.get(key),
    [searchParams],
  );

  const entries = useCallback(() => {
    const list: Array<[string, string]> = [];
    searchParams.forEach((v, k) => list.push([k, v]));
    return list;
  }, [searchParams]);

  return { get, enqueue, entries } as const;
}

function serialize<T>(value: T | undefined): string | undefined {
  if (value === undefined || value === null) return undefined;
  return String(value);
}

// Sorting encode/decode helpers
export type ColumnSort = { id: string; desc: boolean };
export type SortingState = ColumnSort[];

function encodeSorting(state: SortingState): string {
  return state
    .filter((s) => s && typeof s.id === 'string' && s.id.length > 0)
    .map((s) => (s.desc ? `-${s.id}` : s.id))
    .join(',');
}

function parseSorting(
  raw: string | null | undefined,
): SortingState | undefined {
  if (!raw) return undefined;
  const parts = raw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const parsed: SortingState = parts
    .map((token) => {
      if (!token) return undefined;
      const desc = token.startsWith('-');
      const id = desc ? token.slice(1) : token;
      if (!id) return undefined;
      return { id, desc } as ColumnSort;
    })
    .filter((v): v is ColumnSort => !!v);
  return parsed.length ? parsed : undefined;
}

/*
  Store table state in the URL search params
  Example: ?users[page]=2&users[per]=20
*/
export function useUrlTableStateManager(
  parameterKey: string,
): TableStateManager {
  const keyFor = useCallback(
    (stateKey: string) => `${parameterKey}-${stateKey}`,
    [parameterKey],
  );

  const filterKeyFor = useCallback(
    (filterId: string) => `${keyFor('filter')}-${filterId}`,
    [keyFor],
  );

  const { enqueue, get, entries } = useSearchParamsBatcher();

  function parseJsonSafe<T>(raw: string | null | undefined): T | undefined {
    if (!raw) return undefined;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return undefined;
    }
  }

  return {
    usePaginationState(initialState) {
      const pageParam = useMemo(() => {
        const v = get(keyFor('page'));
        return v ? safeNumber(v) : undefined;
      }, [get, keyFor]);

      const perParam = useMemo(() => {
        const v = get(keyFor('per'));
        return v ? safeNumber(v) : undefined;
      }, [get, keyFor]);

      const state: PaginationState = {
        pageIndex: pageParam ?? initialState.pageIndex,
        pageSize: perParam ?? initialState.pageSize,
      };

      const setPaginationState: Setter<PaginationState> = (updater) => {
        const newState = isUpdaterFn(updater) ? updater(state) : updater;
        enqueue((sp) => {
          const page = serialize(newState.pageIndex);
          const per = serialize(newState.pageSize);
          const pageKey = keyFor('page');
          const perKey = keyFor('per');
          if (page === undefined) sp.delete(pageKey);
          else sp.set(pageKey, page);
          if (per === undefined) sp.delete(perKey);
          else sp.set(perKey, per);
        });
      };

      return [state, setPaginationState];
    },

    useSortingState(initialState: SortingState) {
      const sortParam = useMemo(
        () => parseSorting(get(keyFor('sort'))),
        [get, keyFor],
      );
      const state: SortingState = sortParam ?? initialState;
      const setSortingState: Setter<SortingState> = (updater) => {
        const newState = isUpdaterFn(updater) ? updater(state) : updater;
        enqueue((sp) => {
          const key = keyFor('sort');
          if (!newState || newState.length === 0) {
            sp.delete(key);
          } else {
            sp.set(key, encodeSorting(newState));
          }
        });
      };
      return [state, setSortingState];
    },

    useColumnFiltersState(initialState: ColumnFiltersState) {
      // filters are URL-backed
      const filtersFromUrl: ColumnFiltersState = useMemo(
        () =>
          entries()
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([key, value]) => key.startsWith(keyFor('filter')))
            .map(([key, value]) => ({
              id: key.replace(keyFor('filter-'), ''),
              value: parseJsonSafe<unknown>(value),
            })),
        [get, keyFor, entries],
      );

      const state: ColumnFiltersState = filtersFromUrl ?? initialState;

      const setFilteringState: Setter<ColumnFiltersState> = (updater) => {
        const next = isUpdaterFn(updater) ? updater(state) : updater;

        // 2) write only the column filters to the URL (batched)
        enqueue((sp) => {
          const nextFilters = next;

          entries()
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([key]) => key.startsWith(keyFor('filter')))
            .forEach(([key]) => sp.delete(key));

          nextFilters.forEach((filter) => {
            const key = filterKeyFor(filter.id);
            sp.set(key, JSON.stringify(filter.value));
          });
        });
      };

      return [state, setFilteringState];
    },
  };
}
